/**
 * Merges two relative URL paths, removing duplicate overlapping segments.
 *
 * @param basePath - The base path (e.g., '/rest/api/v1/rc')
 * @param appendPath - The path to append (e.g., 'rc/utils/cities')
 * @returns The merged path without duplicate segments (e.g., '/rest/api/v1/rc/utils/cities')
 *
 * @example
 * mergePaths('/rest/api/v1/rc', 'rc/utils/cities')
 * // Returns: '/rest/api/v1/rc/utils/cities'
 *
 * @example
 * mergePaths('/api/v1', 'v1/users')
 * // Returns: '/api/v1/users'
 */
export function mergePaths(basePath: string, appendPath: string): string {
  // Split paths by '/' and filter out empty strings
  const baseSegments = basePath.split('/').filter(Boolean);
  const appendSegments = appendPath.split('/').filter(Boolean);

  // If either path is empty, return the non-empty one
  if (baseSegments.length === 0) {
    return appendPath;
  }
  if (appendSegments.length === 0) {
    return basePath;
  }

  // Find overlapping segments at the boundary
  // Check if any starting segments of appendPath match the ending segments of basePath
  let overlapLength = 0;
  for (let i = 1; i <= Math.min(baseSegments.length, appendSegments.length); i++) {
    // Check if the last i segments of basePath match the first i segments of appendPath
    if (
      baseSegments.slice(-i).join('/') === appendSegments.slice(0, i).join('/')
    ) {
      overlapLength = i;
    }
  }

  // Remove the overlapping segments from appendPath
  const mergedSegments = [
    ...baseSegments,
    ...appendSegments.slice(overlapLength),
  ];

  // Reconstruct the path, preserving leading slash if basePath had one
  const result = mergedSegments.join('/');
  return basePath.startsWith('/') ? '/' + result : result;
}
